import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, useRef } from "react";
import {X} from 'lucide-react';
import { trackPopupInteraction, trackExitIntent, initTimeOnPageTracking } from "../../utils/trackingHelpers";

interface TrialPopupFlowProps {
  onActivate: (planType: 'starter-mensual' | 'enterprise-mensual' | 'enterprise-anual' | null, couponCode?: string) => void;
}

export const TrialPopupFlow = ({ onActivate }: TrialPopupFlowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutos
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [userEngaged, setUserEngaged] = useState(false);
  const getTimeOnPage = initTimeOnPageTracking();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const engagementCountRef = useRef(0);
  const startYRef = useRef(0);

  // Countdown timer - solo inicia cuando popup está abierto
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // Función optimizada para activar popup con vibración y feedback
  const triggerPopup = useCallback((trigger: string) => {
    if (hasShown) return;

    // Vibración suave en móviles (mejor UX)
    if ('vibrate' in navigator) {
      navigator.vibrate([100, 50, 100]);
    }

    // Tracking
    trackPopupInteraction('trial_popup_50off', 'viewed');
    trackPopupInteraction('trial_popup_50off', `triggered_by_${trigger}`);
    
    setIsOpen(true);
    setHasShown(true);

    // Limpiar timer si existe
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [hasShown]);

  // Detección de engagement real del usuario
  useEffect(() => {
    if (hasShown || userEngaged) return;

    let debounceTimer: NodeJS.Timeout;
    const ENGAGEMENT_THRESHOLD = 3; // Mínimo 3 interacciones para considerar "engaged"
    const TRIGGER_DELAY = 40000; // 40 segundos después de engagement

    // Handler unificado para todas las interacciones
    const handleEngagement = () => {
      engagementCountRef.current += 1;

      // Debounce para evitar spam
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (engagementCountRef.current >= ENGAGEMENT_THRESHOLD && !userEngaged) {
          setUserEngaged(true);
          console.log('✅ Usuario engaged - Timer iniciado');

          // Iniciar timer SOLO después de engagement real
          timerRef.current = setTimeout(() => {
            triggerPopup('timer_40s_after_engagement');
          }, TRIGGER_DELAY);
        }
      }, 500);
    };

    // Eventos de engagement (más específicos que el código anterior)
    const engagementEvents: Array<keyof DocumentEventMap> = [
      'scroll',
      'click', 
      'touchstart',
      'keydown',
      'mousemove'
    ];

    // Agregar listeners con passive para mejor performance
    engagementEvents.forEach(event => {
      document.addEventListener(event, handleEngagement, { 
        passive: true,
        capture: false 
      });
    });

    // Exit intent para desktop
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown && userEngaged) {
        trackExitIntent('top', getTimeOnPage());
        triggerPopup('exit_intent');
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);

    // Cleanup mejorado
    return () => {
      clearTimeout(debounceTimer);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      engagementEvents.forEach(event => {
        document.removeEventListener(event, handleEngagement);
      });
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [hasShown, userEngaged, triggerPopup, getTimeOnPage]);

  // Handlers optimizados con mejor UX
  const handleClose = useCallback(() => {
    trackPopupInteraction('trial_popup_50off', 'closed');
    setIsOpen(false);
  }, []);

  const handleActivate = useCallback(() => {
    trackPopupInteraction('trial_popup_50off', 'converted');
    setIsOpen(false);
    onActivate('enterprise-mensual', 'DESCUENTO50');
  }, [onActivate]);

  const handleDecline = useCallback(() => {
    trackPopupInteraction('trial_popup_50off', 'declined');
    setIsOpen(false);
  }, []);

  const handleCopyCoupon = useCallback(async () => {
    try {
      await navigator.clipboard.writeText('DESCUENTO50');
      setCopiedCoupon(true);
      trackPopupInteraction('trial_popup_50off', 'coupon_copied');
      
      // Vibración de confirmación en móviles
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
      
      setTimeout(() => setCopiedCoupon(false), 3000);
    } catch (err) {
      console.error('Error al copiar cupón:', err);
      // Fallback: seleccionar texto para copiar manualmente
      const textElement = document.getElementById('coupon-code');
      if (textElement) {
        const range = document.createRange();
        range.selectNodeContents(textElement);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }, []);

  // Soporte para cierre con tecla ESC (accesibilidad)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevenir scroll del body cuando modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Gesture handler para swipe down en móviles (cierre intuitivo)
  const handleTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endY = e.changedTouches[0].clientY;
    const diffY = endY - startYRef.current;
    
    // Si swipe down > 100px, cerrar
    if (diffY > 100) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md px-3 sm:px-4 md:px-6"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
        aria-describedby="popup-description"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ 
            scale: 1, 
            opacity: 1, 
            y: 0,
            transition: {
              type: "spring",
              duration: 0.5,
              bounce: 0.3
            }
          }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 rounded-2xl sm:rounded-3xl w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl max-h-[95vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&h=800&fit=crop&q=80&blur=100)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay',
            scrollbarWidth: 'thin',
            scrollbarColor: '#00C2FF #1a1a1a'
          }}
        >
          {/* Overlay oscuro mejorado */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/96 via-gray-800/93 to-gray-900/96 backdrop-blur-xl"></div>

          {/* Indicador de swipe para móviles */}
          <div className="sticky top-0 z-20 flex justify-center pt-2 pb-1 sm:hidden">
            <div className="w-12 h-1 bg-gray-400/50 rounded-full"></div>
          </div>

          {/* Contenido optimizado mobile-first */}
          <div className="relative z-10 p-4 sm:p-6 md:p-10 lg:p-12">
            {/* Botón Cerrar - Touch-friendly */}
            <button
              onClick={handleClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-full w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-all duration-200 backdrop-blur-sm border border-white/20 z-30"
              aria-label="Cerrar popup"
              type="button"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            {/* Título Principal - Responsivo */}
            <motion.h2
              id="popup-title"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white text-center mb-2 sm:mb-3 md:mb-4 leading-tight px-2"
            >
              ¡Hasta un <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00C2FF] to-[#BC9C71]">50% de Descuento</span>!
            </motion.h2>

            {/* Subtítulo - Responsivo */}
            <motion.p
              id="popup-description"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-base sm:text-lg md:text-xl text-gray-300 text-center mb-4 sm:mb-6 md:mb-8 font-semibold tracking-wide px-2"
            >
              OBTÉN HASTA UN 50% DE DESCUENTO
            </motion.p>

            {/* Código de Cupón - Touch-optimizado */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.35, type: "spring", duration: 0.5 }}
              onClick={handleCopyCoupon}
              className="bg-gradient-to-r from-[#00C2FF]/20 to-[#2A1E42]/20 backdrop-blur-sm border-2 border-[#00C2FF]/50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-5 md:mb-6 text-center cursor-pointer active:scale-95 hover:border-[#00C2FF]/80 transition-all duration-300 touch-manipulation"
              role="button"
              aria-label="Copiar código de cupón"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCopyCoupon();
                }
              }}
            >
              <p className="text-[#00C2FF] text-xs sm:text-sm font-semibold mb-2 tracking-wider uppercase">Usa el código</p>
              <div className="bg-white/10 backdrop-blur-md border-2 border-dashed border-[#BC9C71] rounded-lg px-4 py-2 sm:px-6 sm:py-3 inline-block">
                <p 
                  id="coupon-code"
                  className="text-[#BC9C71] text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black tracking-widest select-all"
                >
                  DESCUENTO50
                </p>
              </div>
              <p className={`text-xs sm:text-sm mt-2 transition-all duration-300 font-medium ${
                copiedCoupon ? 'text-green-400 font-bold scale-105' : 'text-gray-400'
              }`}>
                {copiedCoupon ? '✓ ¡Copiado al portapapeles!' : '👆 Toca para copiar'}
              </p>
            </motion.div>

            {/* Temporizador - Responsivo optimizado */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, type: "spring" }}
              className="flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3 mb-6 sm:mb-8 md:mb-10"
              role="timer"
              aria-live="polite"
              aria-label={`Tiempo restante: ${minutes} minutos y ${seconds} segundos`}
            >
              {/* Minutos - Dígito 1 */}
              <div className="flex flex-col items-center">
                <motion.div
                  key={minutes}
                  initial={{ rotateX: -90, opacity: 0 }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  transition={{ duration: 0.6, type: "spring" }}
                  className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-gray-900 font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 flex items-center justify-center rounded-xl sm:rounded-2xl shadow-2xl border-2 sm:border-3 md:border-4 border-yellow-300"
                  style={{
                    transformStyle: 'preserve-3d',
                    perspective: '1000px'
                  }}
                >
                  {minutes.toString().padStart(2, '0')[0]}
                </motion.div>
                <p className="text-yellow-400 text-[10px] sm:text-xs md:text-sm font-bold mt-1 sm:mt-1.5 md:mt-2 tracking-wider">Min</p>
              </div>

              {/* Minutos - Dígito 2 */}
              <motion.div
                key={`min2-${minutes}`}
                initial={{ rotateX: -90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                transition={{ duration: 0.6, type: "spring", delay: 0.1 }}
                className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-gray-900 font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 flex items-center justify-center rounded-xl sm:rounded-2xl shadow-2xl border-2 sm:border-3 md:border-4 border-yellow-300"
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {minutes.toString().padStart(2, '0')[1]}
              </motion.div>

              {/* Separador */}
              <div className="text-yellow-400 font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mx-0.5 sm:mx-1">:</div>

              {/* Segundos - Dígito 1 */}
              <div className="flex flex-col items-center">
                <motion.div
                  key={Math.floor(seconds / 10)}
                  initial={{ rotateX: -90, opacity: 0 }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  transition={{ duration: 0.6, type: "spring", delay: 0.2 }}
                  className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-gray-900 font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 flex items-center justify-center rounded-xl sm:rounded-2xl shadow-2xl border-2 sm:border-3 md:border-4 border-yellow-300"
                  style={{
                    transformStyle: 'preserve-3d',
                    perspective: '1000px'
                  }}
                >
                  {Math.floor(seconds / 10)}
                </motion.div>
                <p className="text-yellow-400 text-[10px] sm:text-xs md:text-sm font-bold mt-1 sm:mt-1.5 md:mt-2 tracking-wider">Seg</p>
              </div>

              {/* Segundos - Dígito 2 */}
              <motion.div
                key={seconds % 10}
                initial={{ rotateX: -90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                transition={{ duration: 0.6, type: "spring", delay: 0.3 }}
                className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-gray-900 font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 flex items-center justify-center rounded-xl sm:rounded-2xl shadow-2xl border-2 sm:border-3 md:border-4 border-yellow-300"
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {seconds % 10}
              </motion.div>
            </motion.div>

            {/* Botón Principal - Touch-optimizado */}
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={handleActivate}
              className="w-full mb-3 sm:mb-4 md:mb-5 relative group touch-manipulation"
              type="button"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#BC9C71] via-yellow-500 to-[#BC9C71] rounded-xl sm:rounded-2xl blur-sm group-hover:blur-md group-active:blur-lg transition-all duration-300"></div>
              <div className="relative bg-gray-900/90 backdrop-blur-sm border-3 sm:border-4 border-dashed border-[#BC9C71] rounded-xl sm:rounded-2xl py-3 sm:py-4 md:py-5 px-4 sm:px-6 md:px-8 hover:bg-gray-800/90 active:bg-gray-700/90 transition-all duration-300">
                <span className="text-[#BC9C71] text-base sm:text-lg md:text-xl lg:text-2xl font-black tracking-wide uppercase">
                  Obtener 50% Descuento Ahora
                </span>
              </div>
            </motion.button>

            {/* Botón Secundario - Touch-optimizado */}
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={handleActivate}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 text-white text-sm sm:text-base md:text-lg lg:text-xl font-bold py-3 sm:py-3.5 md:py-4 px-4 sm:px-6 md:px-8 rounded-xl shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 mb-3 sm:mb-4 flex items-center justify-center gap-2 touch-manipulation"
              type="button"
            >
              <span className="inline-block w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-white rounded-full animate-pulse"></span>
              <span>AGENDAR DEMO EN VIVO</span>
              <span className="inline-block w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-white rounded-full animate-pulse"></span>
            </motion.button>

            {/* Texto de Rechazo - Touch-friendly */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              onClick={handleDecline}
              className="w-full text-gray-400 hover:text-gray-300 active:text-gray-200 text-xs sm:text-sm md:text-base text-center underline transition-colors duration-300 py-2 touch-manipulation"
              type="button"
            >
              No gracias, prefiero pagar el precio normal...
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
